// Unit formatting for the UI. Real units only: nautical miles, knots, feet, kilograms / tonnes, gallons.
import type { Domain } from '../types';

const GAL_L = 3.78541;
const AVGAS_KG_PER_L = 0.72;
const DIESEL_KG_PER_L = 0.84;

const int = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const one = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const two = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function fmtNm(nm: number): string { return `${int.format(Math.round(nm))} nm`; }
export function fmtKt(kt: number): string { return `${int.format(Math.round(kt))} kn`; }
export function fmtFt(ft: number): string { return `${int.format(Math.round(ft / 100) * 100)} ft`; }
export function fmtRpm(rpm: number): string { return `${int.format(Math.round(rpm / 10) * 10)} rpm`; }
export function fmtPct(fraction: number): string { return `${int.format(Math.round(fraction * 100)) } %`; }

/** 5.5 → '5 h 30 min'; 0.25 → '15 min'; 30 → '1 d 6 h'. */
export function fmtHours(hours: number): string {
  const totalMin = Math.round(hours * 60);
  const d = Math.floor(totalMin / 1440), h = Math.floor((totalMin % 1440) / 60), m = totalMin % 60;
  if (d > 0) return h > 0 ? `${d} d ${h} h` : `${d} d`;
  if (h > 0) return m > 0 ? `${h} h ${m} min` : `${h} h`;
  return `${m} min`;
}

/** Fuel mass in the unit a pilot or skipper reads: gallons for small craft, kg / t for big ones. */
export function fmtFuel(kg: number, domain: Domain, big = false): string {
  if (big) return kg >= 1000 ? `${one.format(kg / 1000)} t` : `${int.format(Math.round(kg))} kg`;
  const kgPerL = domain === 'air' ? AVGAS_KG_PER_L : DIESEL_KG_PER_L;
  const gal = kg / kgPerL / GAL_L;
  return gal >= 100 ? `${int.format(Math.round(gal))} gal` : `${one.format(gal)} gal`;
}

/** Fuel flow: gal/h for small craft, kg/h for big ones. */
export function fmtFlow(kgPerH: number, domain: Domain, big = false): string {
  if (big) return `${int.format(Math.round(kgPerH))} kg/h`;
  const kgPerL = domain === 'air' ? AVGAS_KG_PER_L : DIESEL_KG_PER_L;
  return `${one.format(kgPerH / kgPerL / GAL_L)} gal/h`;
}

/** Specific range: nm per gallon (small) or nm per tonne (big). */
export function fmtSpecificRange(nmPerKg: number, domain: Domain, big = false): string {
  if (big) return `${one.format(nmPerKg * 1000)} nm/t`;
  const kgPerL = domain === 'air' ? AVGAS_KG_PER_L : DIESEL_KG_PER_L;
  return `${two.format(nmPerKg * kgPerL * GAL_L)} nm/gal`;
}

export function fmtIndex(index: number): string { return `${two.format(index)}×`; }
export function fmtCredits(c: number): string { return `${int.format(Math.round(c))} cr`; }

const KW_HP = 1 / 0.7457;
const N_LBF = 1 / 4.4482216;
const KG_LB = 1 / 0.45359237;

/** 134 kW (180 hp) */
export function fmtPower(kw: number): string { return `${int.format(Math.round(kw))} kW (${int.format(Math.round(kw * KW_HP))} hp)`; }
/** 117.0 kN (26,300 lbf) */
export function fmtThrust(n: number): string { return `${one.format(n / 1000)} kN (${int.format(Math.round(n * N_LBF))} lbf)`; }
/** 122 kg (268 lb); tonnes above 10 t */
export function fmtMass(kg: number): string {
  if (kg >= 10000) return `${one.format(kg / 1000)} t`;
  return `${int.format(Math.round(kg))} kg (${int.format(Math.round(kg * KG_LB))} lb)`;
}
/** 0.305 kg/kWh (0.50 lb/hp·h) */
export function fmtBsfc(kgPerKwh: number): string {
  const lbPerHpH = (kgPerKwh * KG_LB) * 0.7457;
  return `${kgPerKwh.toFixed(3)} kg/kWh (${lbPerHpH.toFixed(2)} lb/hp·h)`;
}
/** 0.0632 kg/N·h (0.62 lb/lbf·h) */
export function fmtTsfc(kgPerNh: number): string {
  const lbPerLbfH = (kgPerNh * KG_LB) * 4.4482216;
  return `${kgPerNh.toFixed(4)} kg/N·h (${lbPerLbfH.toFixed(2)} lb/lbf·h)`;
}
/** 20261015 → 2026-10-15 */
export function fmtSeedDate(seed: number): string {
  const s = String(seed).padStart(8, '0');
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}
/** Signed percentage change: +4.2 % / −1.0 % */
export function fmtDelta(ratio: number): string {
  const p = (ratio - 1) * 100;
  const sign = p > 0.05 ? '+' : p < -0.05 ? '−' : '±';
  return `${sign}${one.format(Math.abs(p))} %`;
}
